import {
  forwardRef,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
} from "react"
import { cva } from "class-variance-authority"
import { useTranslation } from "react-i18next"

import { ResizeHandle } from "@/components"
import { useDrawerResize } from "@/hooks"

import {
  PANEL_MAX_WIDTH,
  PANEL_MIN_WIDTH,
  PANEL_MOBILE_MIN_WIDTH,
} from "./constants"
import CopilotLauncher from "./CopilotLauncher"
import type { CopilotSurfaceRenderProps } from "./types"

const containerVariants = cva(
  "fixed inset-y-0 right-0 flex min-h-0 h-full flex-col border-l border-border bg-background shadow-lg",
  {
    variants: {
      isBackgrounded: {
        true: "z-40",
        false: "z-50",
      },
      isResizing: {
        true: "transition-none",
        false: "",
      },
    },
    compoundVariants: [
      {
        isResizing: false,
        className: "transition-[width] duration-200 ease-in-out",
      },
    ],
    defaultVariants: {
      isBackgrounded: false,
      isResizing: false,
    },
  }
)

interface DrawerContainerProps {
  width: number
  isBackgrounded: boolean
  isResizing: boolean
  children: ReactNode
  onResizeStart: (clientX: number) => void
  onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void
}

export interface CopilotDrawerProps {
  surface: CopilotSurfaceRenderProps
  isOpen: boolean
  width: number
  isBackgrounded: boolean
  onOpen: () => void
  onWidthChange: (width: number) => void
  launcherRef?: Ref<HTMLButtonElement>
}

const DrawerContainer = forwardRef<HTMLDivElement, DrawerContainerProps>(
  (
    { width, isBackgrounded, isResizing, onResizeStart, onKeyDown, children },
    ref
  ) => {
    const { t } = useTranslation()

    return (
      <div
        ref={ref}
        data-copilot-panel
        className={containerVariants({ isBackgrounded, isResizing })}
        style={{
          width: `clamp(${PANEL_MOBILE_MIN_WIDTH}px, min(${width}px, 100vw), ${PANEL_MAX_WIDTH}px)`,
        }}
        role="complementary"
        aria-modal={false}
        aria-label={t("common.copilot.aria.assistantLabel")}
      >
        <ResizeHandle
          width={width}
          minWidth={PANEL_MIN_WIDTH}
          maxWidth={PANEL_MAX_WIDTH}
          isResizing={isResizing}
          onResizeStart={onResizeStart}
          onKeyDown={onKeyDown}
        />
        {children}
      </div>
    )
  }
)

DrawerContainer.displayName = "DrawerContainer"

const CopilotDrawer = ({
  surface: { header, content, input },
  isOpen,
  width,
  isBackgrounded,
  onOpen,
  onWidthChange,
  launcherRef,
}: CopilotDrawerProps) => {
  const { t } = useTranslation()
  const drawerRef = useRef<HTMLDivElement>(null)
  const { isResizing, onResizeStart, onResizeKeyDown } = useDrawerResize({
    width,
    onWidthChange,
    enabled: isOpen,
    minWidth: PANEL_MIN_WIDTH,
    maxWidth: PANEL_MAX_WIDTH,
  })

  if (!isOpen) {
    return (
      <div
        className={`fixed bottom-6 right-6 ${isBackgrounded ? "z-40" : "z-60"}`}
        data-copilot-launcher
      >
        <CopilotLauncher
          ref={launcherRef}
          isOpen={false}
          onClick={onOpen}
          ariaLabel={t("common.copilot.aria.assistantLabel")}
          title={t("common.copilot.aria.assistantLabel")}
        />
      </div>
    )
  }

  return (
    <DrawerContainer
      ref={drawerRef}
      width={width}
      isBackgrounded={isBackgrounded}
      isResizing={isResizing}
      onResizeStart={onResizeStart}
      onKeyDown={onResizeKeyDown}
    >
      {header}
      {content}
      <div className="shrink-0">{input}</div>
    </DrawerContainer>
  )
}

export default CopilotDrawer
